import React from 'react';
import * as PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import styled from 'styled-components';
import { STANDARD_RADIUS } from '../../theme/constantUnits';

const StyledButton = styled(MuiButton)`
  && {
    &.loading {
      color: transparent;
      pointer-events: none;
      .loaderContainer {
        position: absolute;
        display: flex;
        //IE11 workaround
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: #fff;
        .progress {
          pointer-events: none;
          color: white;
        }
      }
      .text * {
        color: transparent;
      }

      &.outlined {
        .spinnerLoader {
          & div:after {
            background-color: ${props => props.theme.palette[props.color].main};
          }
        }
      }
    }
  }
`;

const styles = {
  button: {
    borderRadius: STANDARD_RADIUS,
    textTransform: 'none',
    fontWeight: '400'
  },
  button_secondary: {
    backgroundColor: 'button.secondary.background.default',
    color: 'button.secondary.text',
    '&:hover': {
      backgroundColor: 'button.secondary.background.hover'
    },
    '&:active': {
      backgroundColor: 'button.secondary.background.active'
    },
    '&:disabled': {
      backgroundColor: 'button.secondary.background.disabled'
    }
  },
  button_icon: {
    border: 'none'
  },
  moreOptionsButton: {
    border: 'none !important',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline'
    }
  },
  button_outlined: {
    transition: 'background-color .25s, box-shadow .25s, border .25s, color .5s'
  }
};

const SystemButton = props => {
  const {
    loading,
    onClick,
    variant,
    className,
    children,
    fullWidth,
    isIconButton,
    isMoreOptions,
    ...rest
  } = props;

  let sxStyles = { ...styles.button };
  if (props.color === 'secondary') {
    sxStyles = { ...sxStyles, ...styles.button_secondary };
  }
  if (isIconButton) {
    sxStyles = { ...sxStyles, ...styles.button_icon };
  }
  if (isMoreOptions) {
    sxStyles = { ...sxStyles, ...styles.moreOptionsButton };
  }
  if (variant === 'outlined') {
    sxStyles = { ...sxStyles, ...styles.button_outlined };
  }

  return (
    <StyledButton
      {...rest}
      variant={variant}
      onClick={onClick}
      sx={sxStyles}
      className={classNames({ loading }, 'button', className)}
      fullWidth={fullWidth}
      data-testid={props['data-testid'] || 'systemButton'}
      loading={loading ? 1 : 0}
    >
      {loading && (
        <div className={'loaderContainer'}>
          <CircularProgress
            size={20}
            color={'inherit'}
            className={'progress'}
            data-testid={'circularProgress'}
          />
        </div>
      )}
      <span className={'text'}>{children}</span>
    </StyledButton>
  );
};

SystemButton.propTypes = {
  variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
  children: PropTypes.node.isRequired,
  color: PropTypes.oneOf(['primary', 'secondary']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  isIconButton: PropTypes.bool,
  isMoreOptions: PropTypes.bool,
  'data-testid': PropTypes.string,
  'aria-label': PropTypes.string.isRequired
};
SystemButton.defaultProps = {
  color: 'primary',
  variant: 'contained'
};

export default SystemButton;
